import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegisterShortcuts, type KeyboardBindings } from '../lib/keyboard/keyboardScopeContext';

// ─── Constantes compartidas con PaletaComandos ────────────────────────────────

const STORAGE_KEY = 'senciyo:comandos-personalizados';

const RUTAS: Record<string, string> = {
  'nueva-factura':    '/comprobantes/emision?tipo=factura',
  'nueva-boleta':     '/comprobantes/emision?tipo=boleta',
  'nuevo-cliente':    '/clientes?action=create',
  'nuevo-producto':   '/productos?action=create',
  'ir-comprobantes':  '/comprobantes',
  'ir-productos':     '/productos',
  'ir-clientes':      '/clientes',
  'ir-cajas':         '/cajas',
  'ir-cobranzas':     '/cobranzas',
  'ir-indicadores':   '/indicadores',
  'ir-configuracion': '/configuracion',
};

// Atajos de acciones: Ctrl+Shift+tecla (evita conflictos con el navegador)
const ATAJOS_ACCION: Record<string, string> = {
  'f': 'nueva-factura',
  'b': 'nueva-boleta',
  'c': 'nuevo-cliente',
  'p': 'nuevo-producto',
};

// Atajos de navegación: Ctrl+tecla
const ATAJOS_NAVEGACION: Record<string, string> = {
  '1': 'ir-comprobantes',
  '2': 'ir-productos',
  '3': 'ir-clientes',
  '4': 'ir-cajas',
  '5': 'ir-cobranzas',
  '6': 'ir-indicadores',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface AtojoParseado {
  ctrl: boolean;
  shift: boolean;
  alt: boolean;
  key: string;
}

const parsearAtajo = (atajo: string): AtojoParseado | null => {
  const partes = atajo.toLowerCase().split('+').map(p => p.trim());
  const ctrl  = partes.includes('ctrl');
  const shift = partes.includes('shift');
  const alt   = partes.includes('alt');
  const key   = partes.find(p => !['ctrl', 'shift', 'alt', 'meta'].includes(p));
  if (!key) return null;
  return { ctrl, shift, alt, key };
};

interface ComandoPersonalizado {
  id: string;
  atajo: string;
  accionId: string;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useComandosTeclado = (onAbrirPaleta: () => void) => {
  const navigate = useNavigate();

  const ejecutar = useCallback((accionId: string) => {
    const ruta = RUTAS[accionId];
    if (ruta) navigate(ruta);
  }, [navigate]);

  const bindingsPaleta: KeyboardBindings = {
    'ctrl+k': () => onAbrirPaleta(),
  };

  const bindingsNavegacion: KeyboardBindings = {};

  Object.entries(ATAJOS_ACCION).forEach(([tecla, accionId]) => {
    bindingsNavegacion[`ctrl+shift+${tecla}`] = () => ejecutar(accionId);
  });

  Object.entries(ATAJOS_NAVEGACION).forEach(([tecla, accionId]) => {
    bindingsNavegacion[`ctrl+${tecla}`] = () => ejecutar(accionId);
  });

  const raw = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  if (raw) {
    try {
      const personalizados: ComandoPersonalizado[] = JSON.parse(raw);
      personalizados.forEach((cmd) => {
        const parsed = parsearAtajo(cmd.atajo);
        if (!parsed) return;
        const combo = [parsed.ctrl && 'ctrl', parsed.shift && 'shift', parsed.alt && 'alt', parsed.key]
          .filter(Boolean)
          .join('+');
        bindingsNavegacion[combo] = () => ejecutar(cmd.accionId);
      });
    } catch {
      // localStorage corrupto: se ignoran los comandos personalizados
    }
  }

  // Ctrl+K funciona incluso dentro de campos editables; el resto no, para no interferir con la escritura
  useRegisterShortcuts('comandos-teclado-paleta', bindingsPaleta, { priority: 'global', allowInEditable: true });
  useRegisterShortcuts('comandos-teclado-navegacion', bindingsNavegacion, { priority: 'global', allowInEditable: false });
};
